import React, { useEffect, useState } from "react";
import { TiVendorMicrosoft } from "react-icons/ti";
import { FiMail, FiLock, FiArrowRight, FiArrowLeft } from "react-icons/fi";
import { AiOutlineLock } from "react-icons/ai";
import * as Yup from "yup";
import { useFormik } from "formik";
import axios from "axios";

import { myLocalStorage } from "../../../components/StorageHelper";
import useUserStore from "../../../services/userStore";
import { getClientId } from "../../../services/Auth";
import { registerSessionTokens } from "../../../utils/sessionTokenManager";
import {
  decodeKeycloakToken,
  sendTokenToBackend,
  storeLatestTenant,
} from "../../../utils/tokenHelpers";
import { setInitialSessionTime } from "../../../services/Helpers";
import useAuth from "../../../hooks/useAuth";
import { NGROK } from "../../../APIs";
import UserService from "../../../services/UserService";
import AuthenticationPage from "./components/AuthenticationPage";
import { useNavigate } from "react-router-dom";


const StepIndicator = ({ currentStep }) => {
  return (
    <div className="auth-steps" aria-hidden="true">
      {[1, 2].map((step) => (
        <span key={step} data-active={currentStep === step} />
      ))}
    </div>
  );
};

const AuthSignIn = () => {
  const { signIn } = useAuth();
  const { setAzureRedirectURI } = useUserStore((state) => state);
  const fetchUserData = useUserStore((state) => state.getUserData);
  const navigate = useNavigate();
  // Form states
  const [step, setStep] = useState(1); // 1 = email, 2 = password
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [, setTenantName] = useState("");
  const [realmName, setRealmName] = useState("");
  const [redirectURI, setRedirectURI] = useState("");
  const [ssoConfig, setSsoConfig] = useState(null);
  const [showAzureSSO, setShowAzureSSO] = useState(false);

  // UI states
  const [submitLoading, setSubmitLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [globalLoading, setGlobalLoading] = useState(false);

  // Validation schema
  const emailSchema = Yup.object().shape({
    email: Yup.string()
      .email("Invalid email format")
      .trim("No whitespace allowed")
      .strict(true)
      .required("Email is required"),
  });

  const formik = useFormik({
    initialValues: { email: "" },
    validationSchema: emailSchema,
    onSubmit: () => { },
  });

  // API calls
  const getTenantsInfo = async () => {
    try {
      const axiosInstance = axios.create();
      return await axiosInstance.get(
        `${NGROK}/api/get-tenants?email=${email.trim().toLowerCase()}`
      );
    } catch (error) {
      setTenantName("");
      console.error(error);
    }
  };

  const checkIfUserExist = async (email) => {
    const tenants = await getTenantsInfo(email.toLowerCase().trim());

    if (!tenants || !tenants.data?.length) {
      setTenantName("");
      setSubmitLoading(false);
      return false;
    } else if (tenants?.data?.length) {
      setTenantName(tenants?.data[0].tenantName);
      myLocalStorage.setItem("role", tenants?.data[0].role);
      setSubmitLoading(false);
      return tenants?.data[0];
    }
  };

  const keycloakInitAndLogin = async (realm, email) => {
    try {
      setGlobalLoading(true);
      myLocalStorage.setItem("email", email.toLowerCase().trim());
      myLocalStorage.setItem("realm", realm);
      // Clear lastVisitedPage to ensure redirect to endpoints after login
      myLocalStorage.removeItem("lastVisitedPage");

      await UserService.initKeycloak(realm);
      await UserService.doLogin(email.toLowerCase().trim());
    } catch (error) {
      setGlobalLoading(false);
      console.log(error);
    }
  };

  const findTheSSOConfig = async (currentTenantName) => {


    try {
      const res = await axios.get(`${NGROK}/api/computers/sso-check`, {
        params: { tenantName: currentTenantName },
      });

      if (res.data) {
        myLocalStorage.setItem("ssoConfig", JSON.stringify(res.data));
        setSsoConfig(res?.data?.settingsValue);
      }
    } catch (error) {
      setSsoConfig(null);
      myLocalStorage.removeItem("ssoConfig");
    }
  };
  useEffect(() => {
    findTheSSOConfig("");
  }, []);

  useEffect(() => {
    const resetLoadingState = () => {
      setGlobalLoading(false);
      setSubmitLoading(false);
    };

    window.addEventListener("pageshow", resetLoadingState);

    return () => {
      window.removeEventListener("pageshow", resetLoadingState);
    };
  }, []);

  // Event handlers
  const handleEmailChange = (e) => {
    const val = e.target.value;
    setEmail(val.toLowerCase().trim());
    setSsoConfig(null);
    myLocalStorage.removeItem("ssoConfig");
    formik.handleChange(e);
    setSubmitLoading(false);
    setIsSubmitted(false);
    setEmailErrorMessage("");
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    setIsSubmitted(false);
    setPasswordError("");
  };

  const handleBackToEmail = () => {
    setStep(1);
    setPassword("");
    setPasswordError("");
    setIsSubmitted(false);
    setShowAzureSSO(false);
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);
    setIsSubmitted(true);
    formik.handleSubmit();
    setEmailErrorMessage("");

    if (!formik.errors.email && email) {
      const user = await checkIfUserExist(email.toLowerCase().trim());
      if (!user) {
        setEmailErrorMessage("This user doesn't exist.");
        setSubmitLoading(false);
        return;
      }

      // Handle SYS_ADMIN
      if (user.role === "SYS_ADMIN") {
        myLocalStorage.setItem("email", email.toLowerCase().trim());
        setTenantName(user.realmName);
        setRealmName(user.legacyRealm.realmName.toLowerCase());
        setStep(2);
        setSubmitLoading(false);
        return;
      }

      // Handle Azure SSO
      if (user.isFromSSO === "azureSSO") {
        setRedirectURI(user.redirectURL);
        setShowAzureSSO(true);
        setSubmitLoading(false);
        return;
      }

      // Handle Keycloak
      if (user.realm?.keycloakAccountExists) {
        const realm =
          user.role === "TENANT_USER"
            ? user.realm.realmName
            : "whiteswan_tenants";
        keycloakInitAndLogin(realm, email.toLowerCase().trim());
      } else if (user.legacyRealm?.keycloakAccountExists) {
        const realm =
          user.role === "TENANT_USER"
            ? user.legacyRealm.realmName
            : "whiteswan_tenants_legacy";
        keycloakInitAndLogin(realm, email.toLowerCase().trim());
      } else {
        setEmailErrorMessage("This user doesn't exist.");
        setSubmitLoading(false);
      }
    } else {
      setSubmitLoading(false);
    }

    setIsSubmitted(true);
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitted(true);
    setSubmitLoading(true);
    const clientId = getClientId(realmName);

    try {
      const loginData = {
        username: email,
        password,
        realmName,
        clientId,
      };

      const { access_token, refresh_token, userInfo } = await signIn(loginData);

      registerSessionTokens(access_token, refresh_token);
      myLocalStorage.setItem("email", email);
      myLocalStorage.setItem("realm", realmName);
      if (userInfo?.role) {
        myLocalStorage.setItem("role", userInfo.role);
      }
      // Clear lastVisitedPage to ensure redirect to endpoints after login
      myLocalStorage.removeItem("lastVisitedPage");

      const { exp } = decodeKeycloakToken(access_token);
      await sendTokenToBackend(realmName, email, userInfo.role, access_token, exp);
      setInitialSessionTime(30);
      await storeLatestTenant();
      await fetchUserData(email, { force: true });
      navigate("/endpoints");
    } catch (error) {
      console.error(error);
      setPasswordError("Wrong password");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleAzureSSO = async () => {
    myLocalStorage.setItem("email", email.toLowerCase().trim());
    myLocalStorage.setItem("realm", "whiteswan_tenants_legacy");
    // Clear lastVisitedPage to ensure redirect to endpoints after login
    myLocalStorage.removeItem("lastVisitedPage");
    setAzureRedirectURI(redirectURI);
    window.location.href = redirectURI;
  };

  const handleSSOSignIn = async () => {
    if (ssoConfig) {
      let realmName = ssoConfig;
      let clientId = `otp-auth-${realmName}`;

      myLocalStorage.setItem("email", "");
      myLocalStorage.setItem("role", "TENANT_USER");
      myLocalStorage.setItem("realm", realmName);
      myLocalStorage.setItem("clientId", clientId);
      keycloakInitAndLogin(realmName, email.toLowerCase().trim());
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      if (step === 1) {
        handleEmailSubmit(e);
      } else {
        handlePasswordSubmit(e);
      }
    }
  };

  if (globalLoading) {
    return (
      <AuthenticationPage>
        <div className="auth-overlay">
          <div className="auth-spinner" data-tone="blue"></div>
        </div>
      </AuthenticationPage>
    );
  }

  return (
    <AuthenticationPage>
      <div className="auth-card">
        <span className="auth-card-icon">
          <AiOutlineLock />
        </span>
        <h1 className="auth-title">Welcome back</h1>
        <p className="auth-subtitle">
          {showAzureSSO
            ? "Continue with your organization's Microsoft account."
            : step === 1
              ? "Enter your work email to continue."
              : "Enter your password to sign in."}
        </p>

        {!showAzureSSO && <StepIndicator currentStep={step} />}

        <div className="auth-body">
          {!showAzureSSO && (
            <>
              <form onSubmit={handleEmailSubmit}>
                <label htmlFor="email" className="auth-label">
                  Email address
                </label>
                <div className="auth-input-wrap">
                  <FiMail className="auth-input-icon" />
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={email}
                    onChange={handleEmailChange}
                    onKeyDown={handleKeyDown}
                    disabled={submitLoading || step === 2}
                    placeholder="you@company.com"
                    className="auth-input"
                    autoFocus={step === 1}
                  />
                  {step === 1 && (
                    <button
                      type="submit"
                      disabled={submitLoading || !email}
                      className="auth-inline-submit"
                      aria-label="Continue"
                    >
                      {submitLoading ? <span className="auth-spinner"></span> : <FiArrowRight />}
                    </button>
                  )}
                </div>

                {step === 1 && formik.errors.email && isSubmitted && (
                  <div className="auth-error auth-reveal">{formik.errors.email}</div>
                )}

                {step === 1 && emailErrorMessage && isSubmitted && (
                  <div className="auth-error auth-reveal">{emailErrorMessage}</div>
                )}
              </form>

              {step === 2 && (
                <>
                  <div className="auth-reveal">
                    <button type="button" onClick={handleBackToEmail} className="auth-back">
                      <span>
                        <FiArrowLeft />
                      </span>
                      <span>Change email</span>
                    </button>
                  </div>
                  <form onSubmit={handlePasswordSubmit} className="auth-reveal" data-delay="1">
                    <label htmlFor="password" className="auth-label">
                      Password
                    </label>
                    <div className="auth-input-wrap">
                      <FiLock className="auth-input-icon" />
                      <input
                        type="password"
                        id="password"
                        name="password"
                        value={password}
                        onChange={handlePasswordChange}
                        onKeyDown={handleKeyDown}
                        disabled={submitLoading}
                        placeholder="Enter your password"
                        className="auth-input"
                        autoFocus
                      />
                      <button
                        type="submit"
                        disabled={submitLoading || !password}
                        className="auth-inline-submit"
                        aria-label="Sign in"
                      >
                        {submitLoading ? <span className="auth-spinner"></span> : <FiArrowRight />}
                      </button>
                    </div>
                    {passwordError && isSubmitted && <div className="auth-error auth-reveal">{passwordError}</div>}
                  </form>
                </>
              )}
            </>
          )}

          {showAzureSSO && (
            <>
              <div className="auth-reveal">
                <button type="button" onClick={handleBackToEmail} className="auth-back">
                  <span>
                    <FiArrowLeft />
                  </span>
                  <span>{email}</span>
                </button>
              </div>
              <button type="button" onClick={handleAzureSSO} className="auth-primary auth-reveal" data-delay="1">
                <TiVendorMicrosoft />
                Sign in with Microsoft
              </button>
            </>
          )}

          {!showAzureSSO && ssoConfig && (
            <>
              <div className="auth-divider auth-reveal">OR</div>
              <button type="button" onClick={handleSSOSignIn} className="auth-secondary auth-reveal" data-delay="1">
                Sign in with SSO
              </button>
            </>
          )}
        </div>

        <div className="auth-footer">
          <span>
            Don't have an account?{" "}
            <button type="button" onClick={() => navigate("/signUp")} className="auth-link">
              Sign up
            </button>
          </span>
          <a href="/forgot-password" className="auth-link">
            Forgot password?
          </a>
        </div>
      </div>
    </AuthenticationPage>
  );
};

export default AuthSignIn;
